import { queryOptions } from '@tanstack/react-query';

import MGRAPI from '@/interceptors/MgrApi';
import { queryClient } from '@/queryClient';

/** The Datanomik person assigned to the client, as the support panel presents them. */
export interface AccountManager {
  name: string;
  role: string;
  email: string;
  linkedinUrl: string;
  photo: string;
}

/** The client of the session, as `/clients/profile` answers it. Only what the app reads is typed. */
export interface ClientProfile {
  company?: string;
  email?: string;
  country?: string;
  operationCountry?: string;
  baseCurrency?: string;
  operationCurrency?: string;
  requiresOnboarding?: boolean;
  secondaryContact?: { id: string };
  accountManager?: AccountManager;
}

const STALE_TIME_MS = 30 * 1000;

/**
 * The one definition of the profile query. Hooks spread it into `useQuery` and imperative code
 * hands it to `fetchQuery`, so every reader lands on the same cache entry: one request per
 * `STALE_TIME_MS` however many screens, panels and widgets ask, and one key to invalidate.
 */
export const clientProfileQuery = queryOptions({
  queryKey: ['clientProfile'],
  queryFn: () => MGRAPI.get<ClientProfile>('/clients/profile'),
  staleTime: STALE_TIME_MS,
});

/** The profile for code outside React. Legacy callers edit what they get, so the cache keeps its own copy. */
export const getClientProfile = (): Promise<ClientProfile> => (
  queryClient.fetchQuery(clientProfileQuery).then((profile) => ({ ...profile }))
);

/** Saves the company data and drops the cached profile, so every reader picks up the change. */
export const updateClientProfile = (data: Record<string, unknown>) => (
  MGRAPI.put('/clients', data).then((response) => {
    queryClient.invalidateQueries({ queryKey: clientProfileQuery.queryKey });
    return response;
  })
);
